// League history and the record book (HISTORY.md, scenario H5): champions by
// season, the all-time table, the record book, and head-to-head. The league's
// view of the same past the trophy rooms show one manager at a time.
//
// One screen for a practice league and a real one; a real league reads
// `GET /history` (PLAN-2026-09-26-every-screen-and-crayon P3). Drawn through
// the kit.
//
// A TITLE AND A TITLED PANEL EACH (B-390): the screen says what it is and
// every section is a `Panel`, as the redesigned pages are (DETAIL_LEVELS
// part 4); they were captions over cards under no title at all.

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { valueOf, type HistoryView } from '@fsp/domain';
import { useDraftRecap, useHistory, useLeagueBase } from '../data/hooks';
import { TeamMark } from '../kit';
import { Panel } from '../kit/Panel';
import { TableScroll } from '../kit/TableScroll';
import {
  Button,
  Caption,
  Choice,
  Cover,
  Line,
  ListRow,
  RowName,
  RowSub,
  Segments,
  Stack,
  Text,
  Title,
} from '../kit/ui';
import { sortRows } from '../kit/SortHead';
import { useTier } from '../shell/useTier';
import { DashboardScreen } from '../templates/DashboardScreen';
import { Cup, TitleCrown } from './shared/AwardKit';
import { FromLeague } from './shared/FromLeague';
import { TeamName } from './shared/Names';
import { DetailSheet } from '../templates/DetailSheet';
import '../styles/screens/history.css';
import { useTried } from '../data/tried';

export function HistoryScreen() {
  return <FromLeague snapshot={useHistory()}>{(view) => <HistoryBody view={view} />}</FromLeague>;
}

const record = (w: number, l: number, t: number) => `${w}–${l}${t ? `–${t}` : ''}`;

type AllTimeOrder = 'rank' | 'titles' | 'points' | 'playoffs';

function HistoryBody({ view }: { view: HistoryView }) {
  useTried('record-book');
  // A history page whose draft panel is missing for the moment the recap takes
  // to arrive is fine; one that waits on it is not.
  const draft = valueOf(useDraftRecap());
  const navigate = useNavigate();
  const base = useLeagueBase();
  const phone = useTier() === 'phone';
  const room = (teamId: string) => navigate(`${base}/awards?tab=room&team=${teamId}`);
  // THE ALL-TIME TABLE IN ANOTHER ORDER (B-377): its own by record, or by
  // titles, points or trips to the playoffs, highest first; a tie keeps the
  // table's order.
  const [chosenOrder, setOrder] = useState<AllTimeOrder>('rank');
  // Trips to the playoffs are not on a phone's rows, so not its order there.
  const order: AllTimeOrder = phone && chosenOrder === 'playoffs' ? 'rank' : chosenOrder;
  const allTime = sortRows(
    view.allTime,
    order === 'rank'
      ? null
      : order === 'titles'
        ? (r) => r.titles
        : order === 'points'
          ? (r) => r.pointsFor
          : (r) => r.playoffTrips,
    'high',
    false,
  );

  // The league's first season: the oldest it remembers, or this one.
  const since = Math.min(view.league.season, ...view.seasons.map((s) => s.season));

  return (
    <DashboardScreen
      widgets={[
        {
          id: 'head',
          pinned: true,
          content: (
            <Stack gap={0}>
              <Title>History</Title>
              <Text tone="secondary" size="xs" block>
                {view.league.name} since {since}
              </Text>
            </Stack>
          ),
        },
        {
          id: 'champions',
          column: 1,
          content: (
            <Panel title="Champions">
              {view.seasons.length === 0 ? (
                <div className="fsp-history-empty">
                  <Text tone="secondary">
                    Nobody yet. The first champion is crowned when this season’s final is played.
                  </Text>
                </div>
              ) : (
                view.seasons.map((s) => (
                  <ListRow
                    key={s.season}
                    tall
                    leadSize="lg"
                    lead={
                      <Text weight="bold" numeric>
                        {s.season}
                      </Text>
                    }
                    trail={
                      s.champion ? (
                        <Cup
                          primary={s.champion.colors.primary}
                          secondary={s.champion.colors.secondary}
                          size={28}
                        />
                      ) : undefined
                    }
                    onClick={s.champion ? () => room(s.champion!.id) : undefined}
                    cover={s.champion ? `${s.champion.name}'s trophy room` : undefined}
                  >
                    {/* The row opens the champion's trophy room; each name
                          opens that team (B-220). */}
                    <RowName>{s.champion ? <TeamName team={s.champion} /> : 'Undecided'}</RowName>
                    <RowSub>
                      {s.champion ? s.champion.ownerName : ''}
                      {s.runnerUp ? (
                        <>
                          {' · over '}
                          <TeamName team={s.runnerUp} />
                        </>
                      ) : null}
                      {s.pointsTitle ? (
                        <>
                          {' · points title '}
                          <TeamName team={s.pointsTitle}>{s.pointsTitle.ownerName}</TeamName>
                        </>
                      ) : null}
                    </RowSub>
                  </ListRow>
                ))
              )}
              {/* EACH PAST SEASON'S OWN PAGES, for a season another season's
                  league played and the reader was in (H6, B-530). */}
              {view.seasons.some((s) => s.leagueId !== null) ? (
                <Line gap={2} wrap className="fsp-history-seasons">
                  {view.seasons.flatMap((s) =>
                    s.leagueId === null
                      ? []
                      : [
                          <Button
                            key={s.season}
                            to={`/l/${s.leagueId}`}
                            variant="quiet"
                            size="xs"
                            iconRight="next"
                          >
                            The {s.season} season
                          </Button>,
                        ],
                  )}
                </Line>
              ) : null}
            </Panel>
          ),
        },
        {
          id: 'records',
          column: 1,
          content: (
            <Panel title="Record book">
              {view.records.length === 0 ? (
                <div className="fsp-history-empty">
                  <Text tone="secondary">Records start with the first final.</Text>
                </div>
              ) : (
                <div className="fsp-records fsp-history-records">
                  {view.records.map((r) => (
                    <div key={r.id} className="fsp-surface fsp-record fsp-cover-host">
                      <Cover
                        label={`${r.name}: ${r.holder.name}'s trophy room`}
                        onClick={() => room(r.holder.id)}
                      />
                      <Caption>{r.name}</Caption>
                      <span className="fsp-num fsp-disp fsp-record-value">{r.valueLabel}</span>
                      <span className="fsp-record-holder">
                        <TeamMark team={r.holder} size={18} />
                        <span className="fsp-name-text">
                          <TeamName team={r.holder} />
                        </span>
                      </span>
                      <Text tone="secondary" size="xs">
                        {r.when}
                      </Text>
                    </div>
                  ))}
                </div>
              )}
            </Panel>
          ),
        },
        {
          id: 'all-time',
          column: 2,
          content: (
            <Panel title="All-time">
              {/* Under the title, not in its head, where the title broke
                  into two lines beside four segments. */}
              {view.allTime.length > 1 ? (
                <div className="fsp-history-order">
                  <Segments
                    label="Order the all-time table by"
                    value={order}
                    onChange={setOrder}
                    items={[
                      { id: 'rank', label: 'Record' },
                      { id: 'titles', label: 'Titles' },
                      { id: 'points', label: 'Points' },
                      ...(phone ? [] : [{ id: 'playoffs' as const, label: 'Playoffs' }]),
                    ]}
                  />
                </div>
              ) : null}
              {allTime.map((r) => (
                <ListRow
                  key={r.team.id}
                  tall
                  leadSize="sm"
                  lead={
                    <Text tone="muted" numeric>
                      {r.rank}
                    </Text>
                  }
                  selected={r.team.isMine}
                  onClick={() => room(r.team.id)}
                  cover={`${r.team.name}'s trophy room`}
                  trail={
                    <span className="fsp-history-line">
                      <Text numeric>{record(r.wins, r.losses, r.ties)}</Text>
                      <Text numeric tone="secondary">
                        {(r.pct * 100).toFixed(0)}%
                      </Text>
                      {phone ? null : (
                        // SPELLED OUT (B-418): "PO" was a code to learn.
                        <Text numeric tone="secondary">
                          {`${r.playoffTrips} ${r.playoffTrips === 1 ? 'playoff trip' : 'playoff trips'}`}
                        </Text>
                      )}
                    </span>
                  }
                >
                  <RowName marks={r.titles ? <TitleCrown titles={r.titles} /> : undefined}>
                    <TeamName team={r.team} />
                  </RowName>
                  <RowSub>
                    {r.team.ownerName} · {r.seasons} season{r.seasons === 1 ? '' : 's'} ·{' '}
                    {r.pointsFor.toFixed(0)} pts
                  </RowSub>
                </ListRow>
              ))}
            </Panel>
          ),
        },
        {
          id: 'h2h',
          column: 3,
          content: <HeadToHead view={view} phone={phone} />,
        },
        ...(draft !== null && draft.available
          ? [
              {
                id: 'draft',
                column: 3 as const,
                content: (
                  <Panel title="This season’s draft">
                    <div className="fsp-history-empty">
                      <Text tone="secondary" size="xs">
                        {draft.headline}
                      </Text>
                    </div>
                    {draft.teams.slice(0, 3).map((t) => (
                      <ListRow
                        key={t.team.id}
                        leadSize="md"
                        lead={
                          <span className="fsp-grade-letter fsp-disp small" data-grade={t.grade}>
                            {t.grade}
                          </span>
                        }
                        onClick={() => room(t.team.id)}
                        cover={`${t.team.name}'s trophy room`}
                      >
                        <RowName>
                          <TeamName team={t.team} />
                        </RowName>
                      </ListRow>
                    ))}
                    <div className="fsp-history-empty">
                      <Button
                        size="xs"
                        variant="quiet"
                        onClick={() => navigate(`${base}/draft-recap`)}
                      >
                        Full recap
                      </Button>
                    </div>
                  </Panel>
                ),
              },
            ]
          : []),
      ]}
    />
  );
}

type Pair = HistoryView['headToHead'][number]['vs'][number];

/**
 * TWO TEAMS' MEETINGS (B-418): what a head-to-head record is made of — this
 * season's games, each opening its matchup, and each past season's record,
 * which is all a finished season keeps.
 */
function Meetings({
  team,
  pair,
  onClose,
}: {
  team: HistoryView['headToHead'][number]['team'];
  pair: Pair;
  onClose: () => void;
}) {
  const base = useLeagueBase();
  const navigate = useNavigate();
  return (
    <DetailSheet opened onClose={onClose} title={`${team.name} against ${pair.opponent.name}`}>
      <Stack gap={3}>
        <Text block>{`All time: ${record(pair.wins, pair.losses, pair.ties)}.`}</Text>
        {pair.games.length > 0 ? (
          <Stack gap={1}>
            <Caption>This season</Caption>
            {pair.games.map((g) => (
              <ListRow
                key={g.matchupId}
                onClick={() => {
                  onClose();
                  navigate(`${base}/matchup/${g.matchupId}`);
                }}
                cover={`Week ${g.period}'s matchup`}
                trail={
                  <Text
                    numeric
                    tone={g.result === 'W' ? 'good' : g.result === 'L' ? 'bad' : 'primary'}
                  >
                    {`${g.result} ${g.score === null ? '—' : g.score.toFixed(1)}–${g.against === null ? '—' : g.against.toFixed(1)}`}
                  </Text>
                }
              >
                <RowName>
                  {`Week ${g.period}${g.kind === 'playoff' ? ', playoffs' : g.kind === 'consolation' ? ', consolation' : ''}`}
                </RowName>
              </ListRow>
            ))}
          </Stack>
        ) : null}
        {pair.seasons.length > 0 ? (
          <Stack gap={1}>
            <Caption>Past seasons</Caption>
            {pair.seasons.map((s) => (
              <ListRow
                key={s.season}
                trail={<Text numeric>{record(s.wins, s.losses, s.ties)}</Text>}
              >
                <RowName>{String(s.season)}</RowName>
              </ListRow>
            ))}
          </Stack>
        ) : null}
        {pair.wins + pair.losses + pair.ties === 0 ? (
          <Text tone="secondary" block>
            They have not met yet.
          </Text>
        ) : null}
      </Stack>
    </DetailSheet>
  );
}

function HeadToHead({ view, phone }: { view: HistoryView; phone: boolean }) {
  const mine = view.headToHead.find((r) => r.team.isMine) ?? view.headToHead[0] ?? null;
  const [teamId, setTeamId] = useState<string | null>(mine?.team.id ?? null);
  // THE PAIR WHOSE MEETINGS ARE OPEN (B-418), by both teams.
  const [open, setOpen] = useState<{ team: string; opponent: string } | null>(null);
  const rows = view.headToHead;
  if (!rows.length) return null;
  const openRow = open === null ? null : (rows.find((r) => r.team.id === open.team) ?? null);
  const openPair = openRow?.vs.find((v) => v.opponent.id === open?.opponent) ?? null;
  const meetings =
    openRow !== null && openPair !== null ? (
      <Meetings team={openRow.team} pair={openPair} onClose={() => setOpen(null)} />
    ) : null;
  if (phone) {
    const row = rows.find((r) => r.team.id === teamId) ?? rows[0]!;
    return (
      <Panel title="Head-to-head">
        <div className="fsp-history-empty">
          <Choice
            label="Team"
            hideLabel
            searchable={false}
            required
            value={row.team.id}
            onChange={setTeamId}
            options={rows.map((r) => ({ value: r.team.id, label: r.team.name }))}
          />
        </div>
        {row.vs.map((v) => (
          <ListRow
            key={v.opponent.id}
            leadSize="sm"
            lead={<TeamMark team={v.opponent} size={20} />}
            onClick={() => setOpen({ team: row.team.id, opponent: v.opponent.id })}
            cover={`${row.team.name} against ${v.opponent.name}`}
            trail={
              <Text
                numeric
                tone={v.wins > v.losses ? 'good' : v.losses > v.wins ? 'bad' : 'primary'}
              >
                {record(v.wins, v.losses, v.ties)}
              </Text>
            }
          >
            <RowName>{v.opponent.name}</RowName>
          </ListRow>
        ))}
        {meetings}
      </Panel>
    );
  }
  return (
    <Panel title="Head-to-head">
      <TableScroll className="fsp-history-grid">
        <table className="fsp-h2h">
          <thead>
            <tr>
              <th />
              {rows.map((r) => (
                <th key={r.team.id} title={r.team.name}>
                  <TeamMark team={r.team} size={18} />
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.team.id} className={r.team.isMine ? 'mine' : ''}>
                <th title={r.team.name}>
                  {/* The letters' door is a finger's 44 by padding (the audit
                      of 2026-10-11: 18 px wide), and their name says the
                      team whole. */}
                  <span className="fsp-history-rowhead">
                    <TeamMark team={r.team} size={18} />
                    <TeamName team={r.team} className="fsp-abbr-link">
                      {r.team.abbreviation}
                    </TeamName>
                  </span>
                </th>
                {rows.map((c) => {
                  if (c.team.id === r.team.id)
                    return (
                      <td key={c.team.id} className="self">
                        —
                      </td>
                    );
                  const v = r.vs.find((x) => x.opponent.id === c.team.id);
                  if (!v) return <td key={c.team.id} />;
                  const cls = v.wins > v.losses ? 'lead' : v.losses > v.wins ? 'trail' : '';
                  return (
                    <td key={c.team.id} className={cls}>
                      {/* THE CELL OPENS ITS MEETINGS (B-418); its name says
                          whose record against whom (B-374). */}
                      <button
                        type="button"
                        className="fsp-linklike fsp-h2h-cell"
                        aria-label={`${r.team.name} against ${c.team.name}, all time ${record(v.wins, v.losses, v.ties)}: their games`}
                        onClick={() => setOpen({ team: r.team.id, opponent: c.team.id })}
                      >
                        {record(v.wins, v.losses, v.ties)}
                      </button>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </TableScroll>
      {meetings}
    </Panel>
  );
}
